import React from "react";
import { Link } from "react-router-dom";
import { Package, Sparkles, ScanLine, FlaskConical, GraduationCap, Globe2, Check } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/ui-bits";
import { PageHero, Section } from "@/components/PageShell";

const BUNDLE = [
  "Customer and diamond intake", "Documentation review", "Botswana-origin record",
  "Ownership and chain-of-custody record", "Unique BB identification number", "Digital passport",
  "Traceability-data integration", "Integration of existing laboratory reports",
  "Integration of available DiamondEye information", "Images and supporting documents",
  "QR verification", "Certificate creation", "Digital record management",
];

export default function Services() {
  return (
    <div>
      <PageHero
        eyebrow="Services"
        title="One bundled certificate service. Two optional additions."
        subtitle="No confusing menu of charges. A single Botswana Brilliance fee covers the complete standard certificate service."
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-6">
          <Reveal>
            <div className="glass rounded-2xl p-8 h-full">
              <div className="flex items-center gap-3">
                <Package className="h-7 w-7 text-bb-gold" strokeWidth={1} />
                <h2 className="font-serif text-3xl">The Botswana Brilliance Bundle</h2>
              </div>
              <p className="text-sm text-bb-silver/60 mt-3">One fee. The complete standard certificate service.</p>
              <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
                {BUNDLE.map((item) => (
                  <div key={item} className="flex items-start gap-2.5">
                    <Check className="h-4 w-4 text-bb-gold mt-0.5 shrink-0" strokeWidth={1.6} />
                    <span className="text-sm text-bb-silver/75">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-bb-silver/45 mt-6 leading-relaxed">
                External laboratory fees remain the customer's responsibility. If requested, Botswana Brilliance can coordinate
                grading through GIA, GCAL, HRD, IGI or another approved laboratory and integrate the completed report.
              </p>
            </div>
          </Reveal>

          <div className="space-y-6">
            <Reveal delay={0.1}>
              <OptionCard icon={Sparkles} title="Optional Botswana Pride Mark"
                body="A verified, invisible engraving applied using GemEx ION technology, only when selected and authorised. The certificate records the engraving ID, date applied, authorised facility and verification method."
                fee="Additional engraving fee" />
            </Reveal>
            <Reveal delay={0.2}>
              <OptionCard icon={ScanLine} title="Optional DiamondEye Verification Product"
                body="Available DiamondEye identity information is integrated into the certificate at no extra bundled cost. The separate DiamondEye product, used to independently verify a physical diamond against its DiamondEye identity, is available for an additional fee."
                fee="Additional product fee" />
            </Reveal>
          </div>
        </div>
      </Section>

      <Section className="pt-0">
        <Reveal><Eyebrow>Also available</Eyebrow></Reveal>
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          {[
            [FlaskConical, "Laboratory coordination", "We can arrange independent grading with GIA, GCAL, HRD, IGI or another approved laboratory and connect the report."],
            [GraduationCap, "Training", "Guidance for participants on submission, identity capture, visibility controls and certificate management."],
            [Globe2, "International market-access support", "Present the verified Botswana journey to approved buyers and retailers worldwide."],
          ].map(([Icon, t, d], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div className="glass rounded-2xl p-7 h-full">
                <Icon className="h-6 w-6 text-bb-gold" strokeWidth={1} />
                <h3 className="mt-5 font-serif text-2xl">{t}</h3>
                <p className="mt-2 text-sm text-bb-silver/65 leading-relaxed">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-12 text-center">
          <Link to="/portal/submit" className="btn-gold rounded-full px-8 py-3.5 text-sm">Register Rough or Submit Polished</Link>
        </div>
      </Section>
    </div>
  );
}

function OptionCard({ icon: Icon, title, body, fee }) {
  return (
    <div className="glass rounded-2xl p-8">
      <div className="flex items-center gap-3">
        <Icon className="h-6 w-6 text-bb-gold" strokeWidth={1} />
        <h3 className="font-serif text-2xl">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-bb-silver/70 leading-relaxed">{body}</p>
      <div className="mt-4 inline-block rounded-full border border-bb-gold/25 bg-bb-gold/5 px-4 py-1.5 text-xs text-bb-gold">{fee}</div>
    </div>
  );
}
